/** Surfaces & Cards — content cards, hero cards, composed cards, metric tiles. */
import { useState } from 'react';
import { CreateBox } from '../../home/cards';
import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronRight,
  Circle,
  Clock,
  FolderOpen,
  MoreHorizontal,
  Play,
  Star,
  Users,
  Eye,
} from 'lucide-react';
import {
  useHover,
  SURFACE,
  SHADOW_REST,
  SHADOW_HOVER,
  TRACK,
  eyebrow,
  title,
  body,
  meta,
  Section,
} from '../shared';
import { Button, Dialog, HeroCard, ProgressRing, GradientNumber } from '@/interfaces/learner/ui';

// ─── Inline card component (design system preview only) ──────────────────────

type CardStatus = 'new' | 'recent' | 'due';

const STATUS_LABEL: Record<CardStatus, string> = {
  new: 'Not yet studied',
  recent: 'Recently studied',
  due: 'Due for revision',
};

const STATUS_STYLE: Record<CardStatus, React.CSSProperties> = {
  new: { color: 'var(--color-warning-text)', background: 'var(--color-warning-subtle)' },
  recent: { color: 'var(--color-text-muted)', background: 'var(--color-surface-muted)' },
  due: { color: 'var(--color-error-text)', background: 'var(--color-error-subtle)' },
};

const STEP_DOTS_MAX = 9;

function PersonalLessonCard({
  lessonTitle,
  steps,
  created,
  folder,
  accentColor,
  status,
  onClick,
}: {
  lessonTitle: string;
  steps: number;
  created: string;
  folder?: string;
  accentColor: string;
  status?: CardStatus;
  onClick?: () => void;
}) {
  const dotCount = Math.min(steps, STEP_DOTS_MAX);
  const hasMore = steps > STEP_DOTS_MAX;
  return (
    <div
      onClick={onClick}
      style={{
        ...SURFACE,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: 0,
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      <div style={{ height: 3, background: accentColor, flexShrink: 0 }} />
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 0, flex: 1 }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8, minHeight: 18 }}
        >
          {folder && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: 'var(--color-text-muted)',
                background: 'var(--color-surface-muted)',
                padding: '1px 7px',
                borderRadius: 9999,
              }}
            >
              {folder}
            </span>
          )}
          {status && status !== 'recent' && (
            <span
              style={{
                marginLeft: folder ? 'auto' : 0,
                fontSize: 10,
                fontWeight: 500,
                padding: '1px 7px',
                borderRadius: 9999,
                ...STATUS_STYLE[status],
              }}
            >
              {STATUS_LABEL[status]}
            </span>
          )}
        </div>
        <h4
          style={{
            fontSize: 15,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'var(--color-text)',
            lineHeight: 1.35,
            marginBottom: 10,
          }}
        >
          {lessonTitle}
        </h4>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {Array.from({ length: dotCount }).map((_, i) => (
              <div
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 9999,
                  background: accentColor,
                  opacity: 0.5 + (i / dotCount) * 0.5,
                }}
              />
            ))}
            {hasMore && (
              <span style={{ fontSize: 10, color: 'var(--color-text-muted)', marginLeft: 2 }}>
                +{steps - STEP_DOTS_MAX}
              </span>
            )}
          </div>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{created}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Stat pill (compact for composed card) ────────────────────────────────────

function StatPill({
  icon: Icon,
  value,
  label,
  accent,
}: {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: React.CSSProperties }>;
  value: string;
  label: string;
  accent?: string;
}) {
  const [h, handlers] = useHover();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 13px 5px 9px',
        background: 'transparent',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 9999,
        cursor: 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        borderColor: h ? 'var(--shell-command-border-hover)' : 'var(--shell-command-border)',
        boxShadow: h ? '0 1px 4px rgba(0,0,0,0.06)' : '0 1px 2px rgba(0,0,0,0.03)',
      }}
      {...handlers}
    >
      <Icon size={14} strokeWidth={2.2} style={{ color: accent ?? 'var(--color-text-muted)' }} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-text)',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </span>
      <span style={meta}>{label}</span>
    </div>
  );
}

// ─── Pill button ──────────────────────────────────────────────────────────────

function PillButton({
  children,
  icon: Icon,
}: {
  children: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number }>;
}) {
  const [h, handlers] = useHover();
  return (
    <button
      type="button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '9px 18px',
        borderRadius: 9999,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'box-shadow 0.15s, transform 0.15s, background 0.15s',
        background: h ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: '#fff',
        border: '1px solid transparent',
        boxShadow: h ? '0 2px 6px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.05)',
        transform: h ? 'translateY(-0.5px)' : undefined,
      }}
      {...handlers}
    >
      {Icon && <Icon size={14} strokeWidth={2} />}
      {children}
    </button>
  );
}

// ─── Content card ─────────────────────────────────────────────────────────────

function ContentCard({
  cardTitle,
  description,
  lessons,
  duration,
  progress,
  accentColor,
  tag,
}: {
  cardTitle: string;
  description: string;
  lessons: number;
  duration: string;
  progress?: number;
  accentColor: string;
  tag?: string;
}) {
  const [h, handlers] = useHover();
  return (
    <div
      style={{
        ...SURFACE,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
        overflow: 'hidden',
        borderColor: h ? 'var(--color-border-strong)' : 'var(--color-surface-border)',
        boxShadow: h ? SHADOW_HOVER : SHADOW_REST,
        transform: h ? 'scale(1.02)' : undefined,
      }}
      {...handlers}
    >
      <div style={{ height: 2, background: accentColor }} />
      <div style={{ padding: 20 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <span style={{ ...eyebrow, color: accentColor }}>
            {lessons} lessons · {duration}
          </span>
          {tag && (
            <span
              className="rounded-full"
              style={{
                ...eyebrow,
                fontSize: 10,
                padding: '2px 8px',
                background: `${accentColor}10`,
                color: accentColor,
              }}
            >
              {tag}
            </span>
          )}
        </div>
        <h3 style={{ ...title, marginBottom: 5 }}>{cardTitle}</h3>
        <p style={{ ...body, marginBottom: progress != null ? 14 : 0 }}>{description}</p>
        {progress != null && (
          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span style={{ ...meta, fontSize: 12, fontWeight: 500 }}>Progress</span>
              <span style={{ ...meta, fontSize: 13, fontWeight: 600, color: accentColor }}>
                {progress}%
              </span>
            </div>
            <div className="overflow-hidden rounded-full" style={{ height: 4, background: TRACK }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: accentColor,
                  transition: 'width 0.6s ease-out',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Metric tile ──────────────────────────────────────────────────────────────

function MetricTile({
  label: text,
  value,
  unit,
  trend,
  accentColor,
}: {
  label: string;
  value: string;
  unit?: string;
  trend?: string;
  accentColor: string;
}) {
  const [h, handlers] = useHover();
  return (
    <div
      style={{
        ...SURFACE,
        display: 'flex',
        overflow: 'hidden',
        cursor: 'default',
        transition: 'box-shadow 0.15s',
        flex: '1 1 0',
        minWidth: 0,
        boxShadow: h ? SHADOW_HOVER : SHADOW_REST,
      }}
      {...handlers}
    >
      <div style={{ width: 2, background: accentColor, flexShrink: 0 }} />
      <div style={{ padding: '14px 16px', minWidth: 0 }}>
        <p style={{ ...eyebrow, marginBottom: 4 }}>{text}</p>
        <div className="flex items-baseline gap-1.5">
          <span
            style={{
              fontSize: 24,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              color: 'var(--color-text)',
              lineHeight: 1,
            }}
          >
            {value}
          </span>
          {unit && <span style={meta}>{unit}</span>}
        </div>
        {trend && (
          <span
            style={{
              display: 'inline-block',
              marginTop: 6,
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--color-success-text)',
              background: 'var(--color-success-subtle)',
              padding: '2px 7px',
              borderRadius: 6,
            }}
          >
            {trend}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Currently studying ──────────────────────────────────────────────────────

// ─── Module Accordion ─────────────────────────────────────────────────────────

function ModuleAccordion() {
  const [open, setOpen] = useState(true);
  const [hHeader, headerHandlers] = useHover();

  const lessons = [
    { num: 1, name: 'What is an Algorithm?', mins: 8, done: true },
    { num: 2, name: 'Big O Notation', mins: 12, done: true },
    { num: 3, name: 'Bubble Sort', mins: 10, done: true },
    { num: 4, name: 'Merge Sort', mins: 15, active: true },
    { num: 5, name: 'Quick Sort', mins: 14, done: false },
  ];

  return (
    <div style={{ ...SURFACE, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          width: '100%',
          padding: '16px 20px',
          background: hHeader ? 'rgba(0,0,0,0.015)' : 'transparent',
          border: 'none',
          cursor: 'pointer',
          transition: 'background 0.15s',
          textAlign: 'left',
        }}
        {...headerHandlers}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: 'color-mix(in srgb, var(--color-info) 10%, transparent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BookOpen size={16} strokeWidth={2} style={{ color: 'var(--color-info)' }} />
        </div>
        <div className="min-w-0 flex-1">
          <p style={{ ...title, fontSize: 16 }}>Module 1: Sorting Fundamentals</p>
          <p style={{ ...meta, marginTop: 2 }}>5 lessons · 59 min · 3 of 5 complete</p>
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: 'var(--color-success-text)',
            background: 'var(--color-success-subtle)',
            padding: '3px 10px',
            borderRadius: 9999,
            flexShrink: 0,
          }}
        >
          60%
        </div>
        <ChevronRight
          size={16}
          strokeWidth={2}
          style={{
            color: 'var(--color-text-muted)',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(90deg)' : undefined,
            flexShrink: 0,
          }}
        />
      </button>

      {open && (
        <div style={{ padding: '0 20px 16px' }}>
          {lessons.map((l, i) => (
            <div
              key={l.num}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 0',
                borderTop: i > 0 ? '1px solid var(--color-surface-border)' : undefined,
              }}
            >
              {l.done ? (
                <CheckCircle2
                  size={18}
                  strokeWidth={2}
                  style={{ color: 'var(--color-success)', flexShrink: 0 }}
                />
              ) : l.active ? (
                <Play
                  size={18}
                  strokeWidth={2}
                  style={{ color: 'var(--color-accent)', flexShrink: 0 }}
                />
              ) : (
                <Circle
                  size={18}
                  strokeWidth={1.5}
                  style={{ color: 'var(--color-text-muted)', opacity: 0.4, flexShrink: 0 }}
                />
              )}
              <span
                style={{
                  ...title,
                  fontSize: 14,
                  fontWeight: l.active ? 600 : 400,
                  color: l.done
                    ? 'var(--color-text-secondary)'
                    : l.active
                      ? 'var(--color-text)'
                      : 'var(--color-text-muted)',
                  flex: 1,
                }}
              >
                {l.name}
              </span>
              <span style={{ ...meta, fontSize: 12, flexShrink: 0 }}>{l.mins} min</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Discover Card (v2) ───────────────────────────────────────────────────────

function DiscoverCardV2({
  cardTitle,
  author,
  lessons,
  duration,
  level,
  rating,
  learners,
  gradientFrom,
  gradientTo,
  enrolled,
  progress,
}: {
  cardTitle: string;
  author: string;
  lessons: number;
  duration: string;
  level: string;
  rating: number;
  learners: number;
  gradientFrom: string;
  gradientTo: string;
  enrolled?: boolean;
  progress?: number;
}) {
  const [h, handlers] = useHover();

  return (
    <div
      style={{
        ...SURFACE,
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        overflow: 'hidden',
        boxShadow: h ? SHADOW_HOVER : SHADOW_REST,
        transform: h ? 'translateY(-2px)' : undefined,
      }}
      {...handlers}
    >
      {/* Gradient cover area */}
      <div
        style={{
          height: 80,
          background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})`,
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          padding: '0 18px 10px',
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.85)',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(8px)',
            padding: '3px 9px',
            borderRadius: 6,
          }}
        >
          {level}
        </span>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 18px 18px' }}>
        <h3 style={{ ...title, fontSize: 16, marginBottom: 3 }}>{cardTitle}</h3>
        <p style={{ ...meta, marginBottom: 14 }}>by {author}</p>

        {/* Stats row */}
        <div className="mb-3 flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <Star size={12} strokeWidth={2} style={{ color: '#e8a840', fill: '#e8a840' }} />
            <span style={{ ...meta, fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
              {rating.toFixed(1)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users size={11} strokeWidth={2} style={{ color: 'var(--color-text-muted)' }} />
            <span style={{ ...meta, fontSize: 11 }}>{learners.toLocaleString()}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <BookOpen size={11} strokeWidth={2} style={{ color: 'var(--color-text-muted)' }} />
            <span style={{ ...meta, fontSize: 11 }}>{lessons}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock size={11} strokeWidth={2} style={{ color: 'var(--color-text-muted)' }} />
            <span style={{ ...meta, fontSize: 11 }}>{duration}</span>
          </div>
        </div>

        {/* Progress bar (if enrolled) */}
        {enrolled && progress != null && (
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span style={{ ...meta, fontSize: 12, fontWeight: 500 }}>Progress</span>
              <span
                style={{ ...meta, fontSize: 12, fontWeight: 600, color: 'var(--color-accent)' }}
              >
                {progress}%
              </span>
            </div>
            <div className="overflow-hidden rounded-full" style={{ height: 4, background: TRACK }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})`,
                  transition: 'width 0.6s ease-out',
                }}
              />
            </div>
          </div>
        )}

        {/* Enrol CTA (if not enrolled) */}
        {!enrolled && (
          <div className="flex items-center justify-between" style={{ marginTop: 2 }}>
            <span
              style={{
                ...meta,
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
              }}
            >
              Free
            </span>
            <div
              className="flex items-center gap-1"
              style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-accent-text)' }}
            >
              View course
              <ArrowRight size={13} strokeWidth={2} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Folder Card ──────────────────────────────────────────────────────────────

function FolderCard({ name, count, color }: { name: string; count: number; color: string }) {
  const [h, handlers] = useHover();
  return (
    <div
      style={{
        ...SURFACE,
        padding: 18,
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        boxShadow: h ? SHADOW_HOVER : SHADOW_REST,
        transform: h ? 'translateY(-1px)' : undefined,
        position: 'relative',
      }}
      {...handlers}
    >
      <div
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          width: 28,
          height: 28,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text-muted)',
        }}
      >
        <MoreHorizontal size={16} strokeWidth={1.8} />
      </div>
      <FolderOpen size={20} strokeWidth={1.8} style={{ color, marginBottom: 10 }} />
      <p style={{ ...title, fontSize: 14 }}>{name}</p>
      <p style={{ ...meta, marginTop: 2 }}>
        {count} lesson{count !== 1 ? 's' : ''}
      </p>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function SurfacesTab() {
  return (
    <>
      <Section label="Metrics">
        <div className="flex flex-wrap gap-3">
          <MetricTile
            label="Weekly progress"
            value="78"
            unit="%"
            trend="+12% vs last week"
            accentColor="var(--color-success)"
          />
          <MetricTile
            label="Lessons done"
            value="142"
            trend="+8 this week"
            accentColor="var(--color-info)"
          />
          <MetricTile label="Avg score" value="91" unit="%" accentColor="var(--color-warning)" />
        </div>
      </Section>

      <Section label="Content Cards">
        <div className="grid gap-4 min-[640px]:grid-cols-2">
          <ContentCard
            cardTitle="Introduction to Algorithms"
            description="Master fundamental sorting, searching, and graph algorithms with hands-on exercises."
            lessons={12}
            duration="4h"
            progress={67}
            accentColor="var(--color-info)"
            tag="Popular"
          />
          <ContentCard
            cardTitle="Organic Chemistry Basics"
            description="Build your understanding of carbon compounds, reactions, and functional groups."
            lessons={8}
            duration="2.5h"
            progress={25}
            accentColor="var(--color-success)"
            tag="New"
          />
          <ContentCard
            cardTitle="Linear Algebra"
            description="Vectors, matrices, eigenvalues, and their applications in data science."
            lessons={15}
            duration="6h"
            accentColor="var(--color-accent)"
          />
          <ContentCard
            cardTitle="Microeconomics"
            description="Supply and demand, market structures, and consumer behaviour theory."
            lessons={10}
            duration="3h"
            accentColor="var(--color-warning)"
          />
        </div>
      </Section>

      <Section label="Hero Cards">
        <div className="flex flex-col gap-4">
          <HeroCard
            leading={<ProgressRing value={42} />}
            eyebrow="Course · 12 lessons"
            eyebrowColor="var(--color-accent)"
            title="Introduction to Algorithms"
            subtitle="Lesson 5 of 12 · ~2.4h remaining"
            actions={
              <>
                <Button variant="secondary" icon={<Eye size={15} strokeWidth={2} />}>
                  Overview
                </Button>
                <Button variant="primary" icon={<Play size={15} strokeWidth={2.5} />}>
                  Resume
                </Button>
              </>
            }
          />
          <HeroCard
            leading={<GradientNumber value={12} />}
            eyebrow="Current streak"
            eyebrowColor="var(--color-warning)"
            title="Day streak"
            subtitle="Your longest was 21 days. Keep going — you're building a habit."
            actions={
              <Button variant="secondary" icon={<ArrowRight size={15} strokeWidth={2} />}>
                View insights
              </Button>
            }
          />
        </div>
      </Section>

      <Section label="Composed — Course Card">
        <div style={{ ...SURFACE, overflow: 'hidden' }}>
          <div style={{ height: 2, background: 'var(--color-accent)' }} />
          <div style={{ padding: 20 }}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 style={{ ...title, fontSize: 16 }}>Introduction to Algorithms</h3>
                <p style={{ ...meta, marginTop: 3 }}>12 lessons · 4 hours · Intermediate</p>
              </div>
              <span
                className="rounded-full"
                style={{
                  ...eyebrow,
                  fontSize: 10,
                  padding: '3px 10px',
                  background: 'var(--color-success-subtle)',
                  color: 'var(--color-success-text)',
                }}
              >
                67% complete
              </span>
            </div>
            <div
              className="mb-5 overflow-hidden rounded-full"
              style={{ height: 4, background: TRACK }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: '67%',
                  background: 'linear-gradient(90deg, var(--color-accent), #b694e8)',
                }}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2.5">
              <StatPill icon={Check} value="8" label="done" accent="var(--color-success)" />
              <StatPill icon={Clock} value="1.8h" label="left" accent="var(--color-info)" />
              <div className="ml-auto">
                <PillButton icon={ArrowRight}>Continue</PillButton>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section label="Course Page — Module Accordion">
        <ModuleAccordion />
      </Section>

      <Section label="Discover — Course Grid">
        <div className="grid gap-4 min-[640px]:grid-cols-2 min-[900px]:grid-cols-3">
          <DiscoverCardV2
            cardTitle="Machine Learning Fundamentals"
            author="Dr. Sarah Chen"
            lessons={18}
            duration="8h"
            level="Intermediate"
            rating={4.8}
            learners={2340}
            gradientFrom="#9b6bda"
            gradientTo="#6b8cda"
          />
          <DiscoverCardV2
            cardTitle="Quantum Physics 101"
            author="Prof. James Hall"
            lessons={10}
            duration="4h"
            level="Beginner"
            rating={4.6}
            learners={890}
            gradientFrom="#1a9a6e"
            gradientTo="#5bb89a"
            enrolled
            progress={35}
          />
          <DiscoverCardV2
            cardTitle="Advanced Calculus"
            author="Maria Garcia"
            lessons={24}
            duration="12h"
            level="Advanced"
            rating={4.9}
            learners={5600}
            gradientFrom="#d44470"
            gradientTo="#e8a040"
          />
        </div>
      </Section>

      <Section label="Personal Lessons — Cards">
        <div className="grid gap-3 min-[640px]:grid-cols-2 min-[900px]:grid-cols-3">
          <PersonalLessonCard
            lessonTitle="Introduction to Neural Networks"
            steps={6}
            created="2 days ago"
            folder="Data Science"
            accentColor="var(--color-accent)"
          />
          <PersonalLessonCard
            lessonTitle="SQL Joins Deep Dive"
            steps={10}
            created="1 week ago"
            accentColor="var(--color-info)"
          />
          <PersonalLessonCard
            lessonTitle="React Hooks Explained"
            steps={8}
            created="3 days ago"
            folder="Web Dev"
            accentColor="var(--color-success)"
          />
        </div>
      </Section>

      <Section label="Create — Personal Lesson Input">
        <div style={{ maxWidth: 440 }}>
          <CreateBox />
        </div>
      </Section>

      <Section label="Personal Lessons — Folders">
        <div className="grid grid-cols-2 gap-3 min-[640px]:grid-cols-4">
          <FolderCard name="Data Science" count={5} color="var(--color-accent)" />
          <FolderCard name="Algorithms" count={12} color="var(--color-info)" />
          <FolderCard name="Web Dev" count={3} color="var(--color-success)" />
          <FolderCard name="Mathematics" count={8} color="var(--color-warning)" />
        </div>
      </Section>

      <Section label="Dialog">
        <DialogDemo />
      </Section>
    </>
  );
}

// ─── Dialog demo ──────────────────────────────────────────────────────────────

function DialogDemo() {
  const [openInfo, setOpenInfo] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);

  return (
    <>
      <p
        style={{
          fontSize: 13,
          color: 'var(--color-text-muted)',
          lineHeight: 1.6,
          marginBottom: 16,
        }}
      >
        Glass-styled modal with blur backdrop. Used for confirmations, content pickers, and any
        action that needs focused attention. Sizes: sm, md, lg, xl.
      </p>

      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="secondary" size="sm" onPress={() => setOpenInfo(true)}>
          Info dialog (sm)
        </Button>
        <Button variant="secondary" size="sm" onPress={() => setOpenConfirm(true)}>
          Confirmation dialog (sm)
        </Button>
      </div>

      {/* Info dialog */}
      <Dialog isOpen={openInfo} onClose={() => setOpenInfo(false)} title="About Revision" size="sm">
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          Spaced repetition helps you remember what you&apos;ve learned by scheduling review
          sessions at optimal intervals. The more you revise, the stronger your recall becomes.
        </p>
      </Dialog>

      {/* Confirmation dialog */}
      <Dialog
        isOpen={openConfirm}
        onClose={() => setOpenConfirm(false)}
        title="Delete this lesson?"
        size="sm"
      >
        <p
          style={{
            fontSize: 14,
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
            marginBottom: 20,
          }}
        >
          This will permanently delete the lesson and all its content. This action cannot be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" size="sm" onPress={() => setOpenConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onPress={() => setOpenConfirm(false)}>
            Delete
          </Button>
        </div>
      </Dialog>
    </>
  );
}
